/**
 * Number and currency formatting utilities for SPLH Dashboard
 *
 * All number, currency, and percentage formatting in the SPLH Dashboard
 * MUST use these utilities for consistency.
 *
 * @module pages/common/dashboards/SPLHDashboard/utils/formatUtils
 */

import { getSPLHConfig } from '../config';

/**
 * Default formatting options (fallback if config not available)
 * @constant {Object}
 */
const DEFAULT_OPTIONS = {
  currencyFormat: 'USD',
  decimalPlaces: 2,
  locale: 'en-US',
};

/**
 * Gets the display config, with fallback to defaults
 *
 * @param {string} [clientId] - Optional client ID to get client-specific config
 * @returns {Object} Display configuration
 */
function getDisplayConfig(clientId) {
  try {
    const config = getSPLHConfig(clientId);
    return {
      ...DEFAULT_OPTIONS,
      ...config?.display,
    };
  } catch {
    return DEFAULT_OPTIONS;
  }
}

/**
 * Formats a number as currency
 *
 * @param {number|string|null|undefined} value - The value to format
 * @param {Object} [options] - Formatting options
 * @param {string} [options.currencyCode] - ISO 4217 currency code (default: from config or 'USD')
 * @param {string} [options.locale] - BCP 47 locale (default: 'en-US')
 * @param {number} [options.minimumFractionDigits] - Minimum decimal places (default: 2)
 * @param {number} [options.maximumFractionDigits] - Maximum decimal places (default: 2)
 * @param {string} [options.clientId] - Client ID for config lookup
 * @returns {string} Formatted currency string, or '-' for invalid values
 *
 * @example
 * formatCurrency(1234.56);
 * // Returns: '$1,234.56'
 *
 * @example
 * formatCurrency(1234.56, { currencyCode: 'EUR', locale: 'de-DE' });
 * // Returns: '1.234,56 €'
 *
 * @example
 * formatCurrency(null);
 * // Returns: '-'
 */
export function formatCurrency(value, options = {}) {
  // Handle null, undefined, NaN
  if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
    return '-';
  }

  // Convert string to number if needed
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if conversion resulted in NaN
  if (isNaN(numValue)) {
    return '-';
  }

  const config = getDisplayConfig(options.clientId);
  const {
    currencyCode = config.currencyFormat,
    locale = config.locale || 'en-US',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numValue);
  } catch {
    // Fallback for invalid currency codes or locales
    return `$${numValue.toFixed(2)}`;
  }
}

/**
 * Formats a number with specified decimal precision
 *
 * @param {number|string|null|undefined} value - The value to format
 * @param {Object} [options] - Formatting options
 * @param {number} [options.decimalPlaces] - Number of decimal places (default: from config or 2)
 * @param {string} [options.locale] - BCP 47 locale (default: 'en-US')
 * @param {boolean} [options.useGrouping] - Use thousand separators (default: true)
 * @param {string} [options.clientId] - Client ID for config lookup
 * @returns {string} Formatted number string, or '-' for invalid values
 *
 * @example
 * formatNumber(1234.5678, { decimalPlaces: 2 });
 * // Returns: '1,234.57'
 *
 * @example
 * formatNumber(1234.5, { decimalPlaces: 0 });
 * // Returns: '1,235'
 *
 * @example
 * formatNumber(null);
 * // Returns: '-'
 */
export function formatNumber(value, options = {}) {
  // Handle null, undefined, NaN
  if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
    return '-';
  }

  // Convert string to number if needed
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if conversion resulted in NaN
  if (isNaN(numValue)) {
    return '-';
  }

  const config = getDisplayConfig(options.clientId);
  const {
    decimalPlaces = config.decimalPlaces,
    locale = config.locale || 'en-US',
    useGrouping = true,
  } = options;

  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
      useGrouping,
    }).format(numValue);
  } catch {
    // Fallback for invalid locales
    return numValue.toFixed(decimalPlaces);
  }
}

/**
 * Formats a number as a percentage
 *
 * @param {number|string|null|undefined} value - The value to format (as decimal, e.g., 0.15 for 15%)
 * @param {Object} [options] - Formatting options
 * @param {boolean} [options.showSign] - Show +/- sign for positive/negative values (default: false)
 * @param {number} [options.decimalPlaces] - Number of decimal places (default: 1)
 * @param {string} [options.locale] - BCP 47 locale (default: 'en-US')
 * @param {boolean} [options.alreadyPercent] - If true, value is already a percentage (not decimal)
 * @param {string} [options.clientId] - Client ID for config lookup
 * @returns {string} Formatted percentage string, or '-' for invalid values
 *
 * @example
 * formatPercentage(0.1567);
 * // Returns: '15.7%'
 *
 * @example
 * formatPercentage(0.1567, { showSign: true });
 * // Returns: '+15.7%'
 *
 * @example
 * formatPercentage(-0.05, { showSign: true, decimalPlaces: 2 });
 * // Returns: '-5.00%'
 *
 * @example
 * formatPercentage(15.67, { alreadyPercent: true });
 * // Returns: '15.7%'
 */
export function formatPercentage(value, options = {}) {
  // Handle null, undefined, NaN
  if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
    return '-';
  }

  // Convert string to number if needed
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if conversion resulted in NaN
  if (isNaN(numValue)) {
    return '-';
  }

  const {
    showSign = false,
    decimalPlaces = 1,
    locale = 'en-US',
    alreadyPercent = false,
  } = options;

  // Convert to percentage if not already
  const percentValue = alreadyPercent ? numValue : numValue * 100;

  try {
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(Math.abs(percentValue));

    // Build result with sign if needed
    let result = '';
    if (showSign && percentValue > 0) {
      result = '+';
    } else if (percentValue < 0) {
      result = '-';
    }

    return `${result}${formatted}%`;
  } catch {
    // Fallback for invalid locales
    const sign = showSign && percentValue > 0 ? '+' : percentValue < 0 ? '-' : '';
    return `${sign}${Math.abs(percentValue).toFixed(decimalPlaces)}%`;
  }
}

/**
 * Formats SPLH (Sales Per Labor Hour) value
 * Convenience function with SPLH-specific defaults
 *
 * @param {number|string|null|undefined} value - The SPLH value to format
 * @param {Object} [options] - Formatting options (same as formatCurrency)
 * @returns {string} Formatted SPLH value as currency
 *
 * @example
 * formatSPLH(45.67);
 * // Returns: '$45.67'
 */
export function formatSPLH(value, options = {}) {
  return formatCurrency(value, options);
}

/**
 * Formats labor hours
 *
 * @param {number|string|null|undefined} value - The hours value to format
 * @param {Object} [options] - Formatting options
 * @param {number} [options.decimalPlaces] - Number of decimal places (default: 1)
 * @param {string} [options.suffix] - Suffix to add (default: ' hrs')
 * @returns {string} Formatted hours string
 *
 * @example
 * formatHours(8.5);
 * // Returns: '8.5 hrs'
 *
 * @example
 * formatHours(40, { suffix: ' hours' });
 * // Returns: '40.0 hours'
 */
export function formatHours(value, options = {}) {
  const { decimalPlaces = 1, suffix = ' hrs' } = options;
  const formatted = formatNumber(value, { decimalPlaces, ...options });
  return formatted === '-' ? formatted : `${formatted}${suffix}`;
}

/**
 * Formats a delta/change value with appropriate sign and color indication
 *
 * @param {number|string|null|undefined} value - The delta value
 * @param {Object} [options] - Formatting options
 * @param {string} [options.format] - Format type: 'currency', 'percentage', 'number' (default: 'currency')
 * @param {boolean} [options.invertColors] - If true, negative is good (green) (default: false)
 * @returns {{ formatted: string, direction: 'up'|'down'|'flat', colorType: 'positive'|'negative'|'neutral' }}
 *
 * @example
 * formatDelta(5.5);
 * // Returns: { formatted: '+$5.50', direction: 'up', colorType: 'positive' }
 *
 * @example
 * formatDelta(-3.2, { format: 'percentage', invertColors: true });
 * // Returns: { formatted: '-3.2%', direction: 'down', colorType: 'positive' }
 */
export function formatDelta(value, options = {}) {
  const { format = 'currency', invertColors = false } = options;

  // Handle null, undefined, NaN
  if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
    return { formatted: '-', direction: 'flat', colorType: 'neutral' };
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return { formatted: '-', direction: 'flat', colorType: 'neutral' };
  }

  // Determine direction
  let direction = 'flat';
  if (numValue > 0.001) {
    direction = 'up';
  } else if (numValue < -0.001) {
    direction = 'down';
  }

  // Determine color type
  let colorType = 'neutral';
  if (direction === 'up') {
    colorType = invertColors ? 'negative' : 'positive';
  } else if (direction === 'down') {
    colorType = invertColors ? 'positive' : 'negative';
  }

  // Format the value
  let formatted;
  switch (format) {
    case 'percentage':
      formatted = formatPercentage(numValue, { showSign: true, alreadyPercent: true });
      break;
    case 'number':
      const sign = numValue > 0 ? '+' : '';
      formatted = `${sign}${formatNumber(numValue)}`;
      break;
    case 'currency':
    default:
      const currencySign = numValue > 0 ? '+' : '';
      formatted = `${currencySign}${formatCurrency(numValue)}`;
      break;
  }

  return { formatted, direction, colorType };
}

/**
 * Formats a compact number (e.g., 1.5K, 2.3M)
 *
 * @param {number|string|null|undefined} value - The value to format
 * @param {Object} [options] - Formatting options
 * @param {string} [options.locale] - BCP 47 locale (default: 'en-US')
 * @param {number} [options.maximumFractionDigits] - Max decimal places (default: 1)
 * @returns {string} Compact formatted number
 *
 * @example
 * formatCompact(1500);
 * // Returns: '1.5K'
 *
 * @example
 * formatCompact(2300000);
 * // Returns: '2.3M'
 */
export function formatCompact(value, options = {}) {
  // Handle null, undefined, NaN
  if (value === null || value === undefined || (typeof value === 'number' && isNaN(value))) {
    return '-';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '-';
  }

  const { locale = 'en-US', maximumFractionDigits = 1 } = options;

  try {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits,
    }).format(numValue);
  } catch {
    // Fallback for unsupported locales
    if (Math.abs(numValue) >= 1000000) {
      return `${(numValue / 1000000).toFixed(maximumFractionDigits)}M`;
    }
    if (Math.abs(numValue) >= 1000) {
      return `${(numValue / 1000).toFixed(maximumFractionDigits)}K`;
    }
    return numValue.toString();
  }
}
